import { Component } from '@theme/component';

const TIMEOUT_MS = 10_000;

/**
 * @typedef {Object} Refs
 * @property {HTMLInputElement} codeInput
 * @property {HTMLInputElement} [pinInput]
 * @property {HTMLButtonElement} button
 * @property {HTMLElement} error
 * @property {HTMLElement} result
 * @property {HTMLElement} balance
 * @extends {Component<Refs>}
 */
export class GiftCardBalance extends Component {
  requiredRefs = ['codeInput', 'button', 'error', 'result', 'balance'];

  /** @type {AbortController | null} */
  #abort = null;

  #lastCheckedKey = '';

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  get #pinPrefixes() {
    return (this.dataset.pinPrefixes || '')
      .split(',')
      .map((prefix) => prefix.trim())
      .filter(Boolean);
  }

  updatePinVisibility() {
    const { codeInput, pinInput } = this.refs;

    if (!pinInput) return;

    const needsPin = this.#pinPrefixes.some((prefix) => codeInput.value.trim().startsWith(prefix));

    pinInput.hidden = !(pinInput.required = needsPin);
  }

  /** @param {SubmitEvent} event */
  async checkBalance(event) {
    event.preventDefault();

    const { codeInput, pinInput, button, error, result, balance } = this.refs;

    const code = codeInput.value.trim();
    const pin = pinInput?.value.trim() || null;
    const key = `${code}|${pin ?? ''}`;

    if (!code || key === this.#lastCheckedKey) return;

    error.hidden = true;
    result.hidden = true;
    button.disabled = true;

    try {
      const data = await this.#fetchBalance(code, pin);

      this.#lastCheckedKey = key;
      balance.textContent = `${data.currency} ${data.currentBalance.toFixed(2)}`;
      result.hidden = false;
    } catch (err) {
      error.textContent = (err instanceof Error && err.message) || '';
      error.hidden = false;
    } finally {
      button.disabled = false;
    }
  }

  /** @param {string} giftcard @param {string | null} pin */
  async #fetchBalance(giftcard, pin) {
    const timeout = new AbortController();
    const timer = setTimeout(() => timeout.abort(), TIMEOUT_MS);

    const signal = AbortSignal.any
      ? AbortSignal.any([this.#abort.signal, timeout.signal])
      : timeout.signal;

    const body = {
      action: 'balance',
      giftvoucherReference: giftcard,
      market: Shopify.country,
      ...(pin && { pin }),
    };

    try {
      const response = await fetch('/apps/giftcard/enquire', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal,
      });

      if (response.status >= 500) throw new Error(Theme.translations.gift_card_server_error);

      if (!response.ok) {
        const message = await response
          .json()
          .then((json) => json.message || '')
          .catch(() => '');
        throw new Error(message);
      }

      const json = await response.json();

      return json.data;
    } catch (err) {
      if (err instanceof TypeError || (err instanceof Error && err.name === 'AbortError')) {
        const error = /** @type {any} */ (new Error(Theme.translations.gift_card_server_error));

        error.cause = err;
        throw error;
      }
      throw err;
    } finally {
      clearTimeout(timer);
    }
  }
}

if (!customElements.get('gift-card-balance')) {
  customElements.define('gift-card-balance', GiftCardBalance);
}
